// Cómo se NOMBRA un adjunto de la caja de prompt —
// ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren.
//
// Los chips eran una miniatura pelada de 64px: un cuadradito con una foto de una prenda
// puede ser el producto que se quiere fotografiar, una imagen de referencia de estilo o
// el modelo, y las tres cosas se ven igual. Acá vive el rótulo que las distingue.
//
// El import del tipo es `import type`: este módulo tiene que quedar PURO (se testea sin
// React ni Supabase) y `agentAttachments` arrastra el cliente de Supabase en runtime.

import type { ChatAttachment } from './agentAttachments';

/**
 * QUÉ es el adjunto, no cómo se llama: "Producto", "Imagen", "Modelo"…
 *
 * Es lo que va en la pastilla del chip. Pose y fondo comparten `kind: 'referencia'` pero
 * se rotulan distinto: para el usuario son dos cosas que no tienen nada que ver —una es
 * la postura de la persona y la otra el escenario— y un "Referencia" común no ayudaría a
 * nadie a entender qué enganchó.
 */
export function etiquetaDeAdjunto(a: ChatAttachment): string {
  switch (a.kind) {
    case 'image':
      return 'Imagen';
    case 'product':
      return 'Producto';
    case 'model':
      return 'Modelo';
    case 'texto':
      return 'Texto';
    case 'referencia':
      return etiquetaDeReferencia(a.tipo);
    case 'moodboard':
      return 'Estilo';
    case 'planilla':
      return 'Planilla';
    case 'pdf':
      return 'PDF';
    case 'video':
      return 'Video';
  }
}

/**
 * Cómo se llama cada clase de REFERENCIA del catálogo, en un solo lugar: estaba copiado
 * como ternario en el chip, la burbuja y su nombre, y un tipo nuevo caía en "Estilo" sin
 * que nada fallara (ADR-2026-10-19-elementos-de-marca).
 */
export function etiquetaDeReferencia(tipo: Extract<ChatAttachment, { kind: 'referencia' }>['tipo']): string {
  switch (tipo) {
    case 'pose':
      return 'Pose';
    case 'fondo':
      return 'Fondo';
    case 'moodboard':
      return 'Estilo';
    case 'elemento':
      return 'Elemento de marca';
    case 'tipografia':
      return 'Tipografía';
  }
}

/**
 * La miniatura de un adjunto, o `null` si no tiene imagen que mostrar (un texto, una
 * planilla). Vive acá porque este ternario estaba COPIADO en cinco lugares, y cada vez que
 * se sumó una clase de adjunto hubo que acordarse de los cinco
 * (ADR-2026-09-08-el-agente-lee-y-escribe-planillas).
 */
export function miniaturaDeAdjunto(a: ChatAttachment): string | null {
  switch (a.kind) {
    case 'image':
      return a.previewUrl;
    case 'product':
    case 'referencia':
    case 'moodboard':
      return a.imageUrl;
    case 'model':
      return a.imageUrl;
    case 'video':
      // Su miniatura, si quien lo adjuntó la tenía (una creación sumada como referencia).
      return a.previewUrl ?? null;
    case 'texto':
    case 'planilla':
    case 'pdf':
      return null;
  }
}

/**
 * Cuántas fotos lleva un moodboard adjunto: "24 fotos" o, si se eligió una parte, "18 de
 * 24 fotos" (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). Sirve para el chip de
 * la caja y para el que queda guardado en el mensaje, que tienen la misma forma.
 */
export function fotosDelMoodboard(a: { cantidadFotos: number; fotoIds?: string[] }): string {
  const total = a.cantidadFotos;
  if (a.fotoIds && a.fotoIds.length < total) return `${a.fotoIds.length} de ${total} fotos`;
  return total === 1 ? '1 foto' : `${total} fotos`;
}

/**
 * Con qué se encabeza la vista ampliada: el nombre propio si lo hay, y si no la
 * etiqueta.
 *
 * Una imagen subida no tiene nombre que mostrar —el archivo se sube con un nombre
 * generado— así que "Imagen" es lo más honesto que se puede poner. El `trim` no es
 * paranoia: un producto puede venir con el nombre en blanco y un título vacío deja la
 * vista sin encabezado.
 */
export function tituloDeAdjunto(a: ChatAttachment): string {
  const nombre = 'name' in a ? a.name?.trim() : '';
  return nombre || etiquetaDeAdjunto(a);
}
